import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import {
  provideBaseAuth,
  buildAuthConfig,
} from '@vasta/frontend-shared-core-authentication';
import { ENVIRONMENT } from '@vasta/env';
import { impersonationInterceptor } from './interceptors/impersonation.interceptor';
import { tenantInterceptor } from './interceptors/tenant.interceptor';
import { unauthorizedInterceptor } from './interceptors/unauthorized.interceptor';
import { AuthFacade } from './facades/auth.facade';
import { ImpersonationService } from './impersonation.service';
import { PreferencesService } from './preferences.service';
import { isPublicPath } from './public-path';

export interface ProvideAuthOptions {
  /**
   * Browser paths that render without a session. The initializer returns
   * before configuring OAuth, so a public page never starts the login flow.
   */
  publicPathPrefixes?: readonly string[];
}

export function provideAuth(
  options: ProvideAuthOptions = {},
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideBaseAuth([
      impersonationInterceptor,
      tenantInterceptor,
      unauthorizedInterceptor,
    ]),
    provideAppInitializer(async () => {
      const baseHref = new URL(document.baseURI).pathname;
      if (
        isPublicPath(
          window.location.pathname,
          options.publicPathPrefixes ?? [],
          baseHref,
        )
      ) {
        return;
      }

      const oauth = inject(OAuthService);
      const auth = inject(AuthFacade);
      const prefs = inject(PreferencesService);
      const env = inject(ENVIRONMENT);
      const impersonation = inject(ImpersonationService);

      // First thing, and before the login flow: the tab may bounce through
      // Zitadel and come back at /auth/callback without the query parameter,
      // and the current user has to be asked for as the target from the start.
      impersonation.captureFromUrl();

      oauth.configure(buildAuthConfig(env.zitadelIssuer, env.zitadelClientId));
      await oauth.loadDiscoveryDocument();
      await oauth.tryLoginCodeFlow();

      if (!oauth.hasValidAccessToken()) {
        oauth.initLoginFlow();
        return;
      }

      auth.loadCurrentUser();
      prefs.load();
    }),
  ]);
}
